import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] max-h-96 overflow-y-auto"
}
const _hoisted_2 = { class: "flex items-center justify-between p-3 border-b border-gray-100 bg-gray-50" }
const _hoisted_3 = { class: "flex items-center gap-2" }
const _hoisted_4 = {
  key: 0,
  class: "h-4 w-4 text-gray-500",
  fill: "none",
  viewBox: "0 0 24 24",
  stroke: "currentColor"
}
const _hoisted_5 = {
  key: 1,
  class: "animate-spin rounded-full h-4 w-4 border-2 border-[#157813] border-t-transparent"
}
const _hoisted_6 = { class: "text-sm font-medium text-gray-700" }
const _hoisted_7 = { class: "p-2" }
const _hoisted_8 = {
  key: 0,
  class: "p-6 text-center text-gray-500"
}
const _hoisted_9 = ["onClick", "onMouseenter"]
const _hoisted_10 = { class: "flex items-center justify-between mb-2" }
const _hoisted_11 = { class: "flex items-center gap-2" }
const _hoisted_12 = { class: "font-semibold text-gray-900" }
const _hoisted_13 = { class: "px-2 py-1 bg-[#157813] text-white text-xs rounded-full" }
const _hoisted_14 = { class: "text-sm text-gray-500" }
const _hoisted_15 = { class: "text-sm text-gray-700 mb-2 font-mono" }
const _hoisted_16 = { class: "flex items-center justify-between" }
const _hoisted_17 = { class: "flex items-center gap-4 text-sm text-gray-500" }
const _hoisted_18 = {
  key: 0,
  class: "flex flex-wrap gap-1 mt-2"
}
const _hoisted_19 = {
  key: 0,
  class: "px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full"
}
const _hoisted_20 = {
  key: 0,
  class: "mt-3 pt-3 border-t border-gray-100 text-center"
}

interface Suggestion {
  id: string;
  type: string;
  text: string;
  author: string;
  runCount: number;
  date: string;
  tags: string[];
  features: string[];
}

interface Props {
  suggestions: Suggestion[];
  isVisible: boolean;
  isLoading: boolean;
  highlightedIndex: number;
  position?: { top: number; left: number; width: number };
  showFooter?: boolean;
  searchText?: string;
}

interface Emits {
  (e: "close"): void;
  (e: "select", suggestion: Suggestion): void;
  (e: "highlight", index: number): void;
  (e: "search"): void;
  (e: "search-click"): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SuggestionDropdown',
  props: {
    suggestions: { default: () => [] },
    isVisible: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    highlightedIndex: { default: -1 },
    position: { default: () => ({ top: 0, left: 0, width: 400 }) },
    showFooter: { type: Boolean, default: false },
    searchText: { default: "" }
  },
  emits: ["close", "select", "highlight", "search", "search-click"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Handle search button click
const handleSearchClick = () => {
  emit('search-click');
};

// Helper functions
const truncateText = (text: string, maxLength: number): string => {
  if (!text) return "";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

const formatDate = (dateString: string): string => {
  if (
    !dateString ||
    dateString === "nan" ||
    dateString === "null" ||
    dateString === "undefined"
  ) {
    return "N/A";
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "N/A";
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    return "N/A";
  }
};

return (_ctx: any,_cache: any) => {
  return (_ctx.isVisible)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            (!_ctx.isLoading)
              ? (_openBlock(), _createElementBlock("svg", _hoisted_4, [...(_cache[1] || (_cache[1] = [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2",
                    d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  }, null, -1)
                ]))]))
              : (_openBlock(), _createElementBlock("div", _hoisted_5)),
            _createElementVNode("span", _hoisted_6, _toDisplayString(_ctx.isLoading
              ? "Searching..."
              : _ctx.suggestions.length > 0
              ? `Found ${_ctx.suggestions.length} matching queries`
              : "No matching queries found"), 1)
          ]),
          _createElementVNode("button", {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close'))),
            class: "text-gray-400 hover:text-gray-600"
          }, [...(_cache[2] || (_cache[2] = [
            _createElementVNode("svg", {
              class: "h-4 w-4",
              fill: "none",
              viewBox: "0 0 24 24",
              stroke: "currentColor"
            }, [
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                "stroke-width": "2",
                d: "M6 18L18 6M6 6l12 12"
              })
            ], -1)
          ]))])
        ]),
        _createElementVNode("div", _hoisted_7, [
          (_ctx.suggestions.length === 0 && !_ctx.isLoading)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [...(_cache[3] || (_cache[3] = [
                _createElementVNode("svg", {
                  class: "h-12 w-12 mx-auto mb-3 text-gray-300",
                  fill: "none",
                  viewBox: "0 0 24 24",
                  stroke: "currentColor"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2",
                    d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  })
                ], -1),
                _createElementVNode("p", { class: "text-lg font-medium" }, "No queries found", -1),
                _createElementVNode("p", { class: "text-sm" }, "Try searching with different keywords", -1)
              ]))]))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.suggestions, (suggestion, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: suggestion.id,
              onClick: ($event: any) => (_ctx.$emit('select', suggestion)),
              onMouseenter: ($event: any) => (_ctx.$emit('highlight', index)),
              class: _normalizeClass(["p-3 border border-gray-100 rounded-lg mb-2 hover:bg-gray-50 cursor-pointer transition-colors", {
          'bg-[#157813]/5': index === _ctx.highlightedIndex,
        }])
            }, [
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("div", _hoisted_11, [
                  _createElementVNode("span", _hoisted_12, "Query ID: " + _toDisplayString(suggestion.id), 1),
                  _createElementVNode("span", _hoisted_13, _toDisplayString(suggestion.type), 1)
                ]),
                _createElementVNode("span", _hoisted_14, _toDisplayString(suggestion.runCount || 0) + " runs", 1)
              ]),
              _createElementVNode("div", _hoisted_15, _toDisplayString(truncateText(suggestion.text, 100)), 1),
              _createElementVNode("div", _hoisted_16, [
                _createElementVNode("div", _hoisted_17, [
                  _createElementVNode("span", null, _toDisplayString(suggestion.author), 1),
                  _createElementVNode("span", null, _toDisplayString(formatDate(suggestion.date)), 1)
                ]),
                _cache[4] || (_cache[4] = _createElementVNode("div", { class: "flex items-center gap-1 text-[#157813] text-sm font-medium" }, [
                  _createElementVNode("span", null, "View Details"),
                  _createElementVNode("svg", {
                    class: "h-4 w-4",
                    fill: "none",
                    viewBox: "0 0 24 24",
                    stroke: "currentColor"
                  }, [
                    _createElementVNode("path", {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      "stroke-width": "2",
                      d: "M9 5l7 7-7 7"
                    })
                  ])
                ], -1))
              ]),
              (suggestion.features && suggestion.features.length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(suggestion.features.slice(0, 3), (feature) => {
                      return (_openBlock(), _createElementBlock("span", {
                        key: feature,
                        class: "px-2 py-1 bg-[#157813] text-white text-xs rounded-full"
                      }, _toDisplayString(feature), 1))
                    }), 128)),
                    (suggestion.features.length > 3)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_19, " +" + _toDisplayString(suggestion.features.length - 3) + " more ", 1))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true)
            ], 42, _hoisted_9))
          }), 128))
        ]),
        (_ctx.suggestions.length > 0 && _ctx.showFooter)
          ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
              _createElementVNode("button", {
                onClick: handleSearchClick,
                class: "text-sm text-white font-medium bg-[#157813] hover:bg-[#0f5a0d] px-3 py-1.5 rounded-lg border border-[#157813] transition-colors"
              }, " Search for \"" + _toDisplayString(_ctx.searchText) + "\" → ", 1)
            ]))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})